import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import {
  ListChecks, Warning, Clock, Calendar as CalIcon, Circle, CheckCircle, Bug, FileDashed, Lightning,
} from '@phosphor-icons/react';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import AppFooter from '../components/AppFooter';

const STATUS_META = {
  todo:        { label: 'To Do',        tone: 'text-zinc-700',   dot: 'bg-zinc-400',    border: 'border-zinc-200' },
  in_progress: { label: 'In Progress',  tone: 'text-blue-700',   dot: 'bg-blue-500',    border: 'border-blue-200' },
  in_review:   { label: 'In Review',    tone: 'text-amber-700',  dot: 'bg-amber-500',   border: 'border-amber-200' },
  done:        { label: 'Done',         tone: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200' },
};
const PRIORITY_COLOR = {
  low: 'bg-zinc-100 text-zinc-600',
  medium: 'bg-amber-50 text-amber-700',
  high: 'bg-orange-50 text-orange-700',
  critical: 'bg-red-50 text-red-700',
};
const WORK_ICON = { task: ListChecks, story: FileDashed, bug: Bug };

const FILTERS = [
  { key: 'all',        label: 'All',          test: () => true },
  { key: 'overdue',    label: 'Overdue',      test: (t) => t.due_date && t.due_date < new Date().toISOString().slice(0, 10) && t.status !== 'done' },
  { key: 'this_week',  label: 'This week',    test: (t) => {
    if (!t.due_date) return false;
    const due = new Date(t.due_date);
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(todayStart); weekEnd.setDate(todayStart.getDate() + 7);
    return due >= todayStart && due <= weekEnd;
  } },
  { key: 'no_due',     label: 'No due date',  test: (t) => !t.due_date },
  { key: 'not_done',   label: 'Not done',     test: (t) => t.status !== 'done' },
];

export default function MyTasksPage() {
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('not_done');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/my-tasks`, { withCredentials: true, headers: getOrgHeaders() });
      setTasks(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, currentOrg, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const fn = FILTERS.find(f => f.key === filter)?.test || (() => true);
    return tasks.filter(fn);
  }, [tasks, filter]);

  // Group by status.
  const groups = useMemo(() => {
    const order = ['todo', 'in_progress', 'in_review', 'done'];
    const map = {};
    for (const t of filtered) {
      const key = t.status || 'todo';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    }
    // Stable order: known statuses first, then alphabetical for custom ones.
    const keys = Object.keys(map).sort((a, b) => {
      const ai = order.indexOf(a), bi = order.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1; if (bi === -1) return -1;
      return ai - bi;
    });
    return keys.map(k => ({ key: k, tasks: map[k] }));
  }, [filtered]);

  const totalSP = useMemo(() => filtered.reduce((s, t) => s + (t.story_points || 0), 0), [filtered]);
  const doneSP = useMemo(() => filtered.filter(t => t.status === 'done').reduce((s, t) => s + (t.story_points || 0), 0), [filtered]);

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="flex-1 overflow-auto bg-zinc-50/50" data-testid="my-tasks-page">
      <div className="bg-white border-b border-zinc-200 px-8 py-5">
        <h1 className="font-['Outfit'] text-[22px] font-bold text-zinc-900 tracking-tight">My Tasks</h1>
        <p className="text-[12px] text-zinc-500 mt-1">Everything assigned to you across every project.</p>
      </div>

      <div className="p-8 max-w-[1400px] mx-auto space-y-5">
        {/* Filter pills + SP total */}
        <div className="flex flex-wrap items-center gap-2" data-testid="my-tasks-filters">
          {FILTERS.map(f => {
            const count = tasks.filter(f.test).length;
            const active = filter === f.key;
            return (
              <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all ${active
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50'}`}>
                {f.label}
                <span className={`text-[10px] tabular-nums ${active ? 'text-blue-100' : 'text-zinc-400'}`}>{count}</span>
              </button>
            );
          })}
          <div className="ml-auto flex items-center gap-4 text-[11px] text-zinc-500 tabular-nums">
            <span><strong className="text-zinc-800">{filtered.length}</strong> task{filtered.length !== 1 ? 's' : ''}</span>
            {totalSP > 0 && <span><strong className="text-zinc-800">{doneSP}</strong> / {totalSP} pts</span>}
          </div>
        </div>

        {loading && <p className="text-center text-[12px] text-zinc-400 py-10">Loading…</p>}

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-xl border border-zinc-200/80 py-16 text-center">
            <CheckCircle size={32} className="mx-auto text-emerald-400 mb-2" weight="fill" />
            <p className="text-[13px] text-zinc-600 font-medium">Nothing to do for this filter</p>
            <p className="text-[11px] text-zinc-400 mt-1">Try another filter or take a break.</p>
          </div>
        )}

        {/* Status groups */}
        {!loading && groups.map(g => {
          const meta = STATUS_META[g.key] || { label: g.key, tone: 'text-zinc-700', dot: 'bg-zinc-400' };
          const grpDone = g.tasks.filter(t => t.status === 'done').length;
          const grpTotal = g.tasks.length;
          const grpSP = g.tasks.reduce((s, t) => s + (t.story_points || 0), 0);
          const pct = grpTotal > 0 ? Math.round((grpDone / grpTotal) * 100) : 0;
          return (
            <div key={g.key} className="bg-white rounded-xl border border-zinc-200/80 overflow-hidden" data-testid={`group-${g.key}`}>
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                <h2 className={`text-[13px] font-bold ${meta.tone}`}>{meta.label}</h2>
                <span className="text-[11px] text-zinc-400 tabular-nums">{grpTotal}</span>
                {grpSP > 0 && <span className="text-[11px] text-zinc-400 tabular-nums">· {grpSP} pts</span>}
                <div className="flex-1 max-w-[160px] ml-auto">
                  <Progress value={pct} className="h-1.5" />
                </div>
              </div>
              <div className="divide-y divide-zinc-50">
                {g.tasks.map(t => {
                  const Icon = WORK_ICON[t.work_type] || ListChecks;
                  const overdue = t.due_date && t.due_date < today && t.status !== 'done';
                  return (
                    <button key={t.task_id} data-testid={`my-task-${t.task_id}`} onClick={() => navigate(`/tasks/${t.task_id}`)}
                      className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-zinc-50/80 text-left transition-colors">
                      <Icon size={14} className={`flex-shrink-0 ${t.work_type === 'bug' ? 'text-red-500' : t.work_type === 'story' ? 'text-emerald-500' : 'text-blue-500'}`} weight="duotone" />
                      <span className="text-[10px] font-mono text-zinc-400 w-14 flex-shrink-0">{t.task_key}</span>
                      <span className={`flex-1 text-[13px] truncate ${t.status === 'done' ? 'line-through text-zinc-400' : 'text-zinc-800'}`}>{t.title}</span>
                      {t.project && <span className="text-[10px] text-zinc-400 hidden md:inline max-w-[140px] truncate">{t.project.name}</span>}
                      {t.story_points != null && <Badge className="text-[10px] bg-indigo-50 text-indigo-700 border-0">{t.story_points} SP</Badge>}
                      {t.priority && <Badge className={`text-[10px] border-0 ${PRIORITY_COLOR[t.priority] || 'bg-zinc-100 text-zinc-600'}`}>{t.priority}</Badge>}
                      {t.due_date && (
                        <span className={`text-[10px] flex items-center gap-0.5 tabular-nums ${overdue ? 'text-red-600 font-semibold' : 'text-zinc-400'}`}>
                          {overdue ? <Warning size={10} weight="fill" /> : <CalIcon size={10} />} {new Date(t.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <AppFooter />
    </div>
  );
}
